import {
  h,
  computed,
  defineComponent,
  onUnmounted,
  ref,
  watch,
  provide,
  Ref,
} from 'vue';
import { hasFeature, hasInspectionQuotaLeft, hasPermission, Permissions, user } from '@/ui/auth/auth';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import ConductInspection from '@/_legacy/views/ConductInspection/ConductInspection.vue';
import { container, loader } from '@/ui/components';
import { useTask } from '@/ui/concerns';
import { filterListToOptions, gtm, IdProps, pushRoute, sequence, toasted } from '@/ui/helpers/uiHelper';
import { EVENTS, EventTypes, trackEvent } from '@/services/activityTracker/activityTrackerHelper';
import { create } from '@/services/api/resources/inspection';
import { legacyConduct } from '@/services/api/resources/checklist';
import { filter as getAllUsers, get as getUser } from '@/services/api/resources/user';
import { SelectOptions } from '@/ui/types';
import { eventBus } from '@/_legacy/eventBus';
import { Events } from '@/ui/events';
import { t } from '@/ui/i18n/i18n';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { now } from '@/_legacy/util/UnixTimestamps';
import { LegacyChecklistRecord } from '@/ui/pages/inspection/LegacyChecklistRecord';
import { Status } from '@/entities/checklist';
import { downloadApp } from '@/ui/components/onboarding/DownloadApp';
import { ApiResponse } from '@/services/api';
import { NamedLocation } from '@/ui/routes/types';
import { EventBus } from '@/ui/EventBus';
import { InspectionNavbar } from '@/ui/pages/inspection/InspectionNavbar';
import { UserDetail } from '@/entities/user';
import * as inspectionAssetsPhotos from '@/services/assets-api/inspectionAssetsPhotos';
import { Userflow, UserflowTrackEvent } from '@/services/userflow/userflow';
import {
  useEntityTypeOfResponseFirstQuestionMapping,
} from '@/ui/concerns/conductInspection/useEntityTypeOfResponseFirstQuestionMapping';
import { INSPECTION_STATUS } from '@/_legacy/constants';
import { processFiles } from '@/services/assets-api/inspectionAssetsFiles';
import { FormsTabNames, queryTabName } from '@/ui/components/navigation/constants';
import { useConductStore } from '@/stores/conduct/conduct';
import { isTranslationEnabled } from '@/ui/helpers/checklistHelper';

//to conduct inspection from checklist/template
export const InspectionCreatePage = defineComponent<IdProps>({
  name: 'InspectionCreatePage',
  props: ['id'],
  setup(props) {
    if (!hasFeature('inspections')) {
      return renderResourceTeaserPage(t('common.inspections'));
    }

    if (!hasPermission(Permissions.CONDUCT_OPEN_INSPECTIONS)) {
      return renderForbiddenPage;
    }

    if (!hasInspectionQuotaLeft.value) {
      toasted.info(t('message.inspection-limit'));
      pushRoute({ name: 'forms' });
    }

    const conductStore = useConductStore();

    const record = ref<LegacyChecklistRecord>();
    const userOptions = ref<SelectOptions>([]);
    const loggedInUserDetail = ref<UserDetail>();

    provide<Ref<SelectOptions>>('userOptions', userOptions);

    // get record from checklist
    const getRecord = useTask(
      () => sequence(legacyConduct(props.id), getAllUsers(), getUser(user.value.id)),
      ([data, users, { data: userData }]) => {
        record.value = {
          inspection_id: null,
          checklist: props.id,
          //@ts-ignore
          checklist_title: data.checklist.checklist_title,
          checklist_title_translated: data.checklist.checklist_title_translated,
          //@ts-ignore
          checklist_description: data.checklist.checklist_description,
          checklist_description_translated: data.checklist.checklist_description_translated,
          //@ts-ignore
          entity_type_of_response_migrated: data.checklist.entity_type_of_response_migrated || false,
          //@ts-ignore
          autofill_entity: data.checklist.autofill_entity || false,
          //@ts-ignore
          blurFaces: data.checklist.blurFaces || false,
          schedule_title: null,
          status: INSPECTION_STATUS.EMPTY,
          entity_item: null,
          //@ts-ignore
          entity_items: data.checklist.entity_items,
          fullTranslationLanguages: data.checklist.fullTranslationLanguages,
          //@ts-ignore
          improveWriting: data.checklist.improveWriting ?? true,
          started_at: now(),
          conducted_at: null,
          //@ts-ignore
          form: data.checklist.form,
          //@ts-ignore
          form_version: data.checklist.form_version || null,
          //@ts-ignore
          todo: data.todo,
          //@ts-ignore
          can_see_all_entity_items: data.can_see_all_entity_items,
        };
        userOptions.value = filterListToOptions(users);
        loggedInUserDetail.value = userData;

        //do not conduct inactive checklist
        if (data.checklist!.status === Status.Inactive) {
          toasted.info(t('common.inactive'));
          pushRoute({ name: 'forms' });
        }

        conductStore.setIsTranslationEnabled(isTranslationEnabled(record.value as LegacyChecklistRecord));
      },
    );
    watch(ref(props.id), getRecord.run);

    // send record
    const sendRecord = (isDraft: boolean) => useTask(
      () => {
        const inspection = {
          ...record.value,
          status: isDraft ? INSPECTION_STATUS.DRAFT : INSPECTION_STATUS.DONE,
        };

        return create(inspection, user.value.timezone);
      },
      (createResult: ApiResponse) => {
        const createdId = (createResult.parsed as { id: number }).id;

        eventBus.$emit(Events.inspectionConducted, { isNew: true });
        trackEvent(EventTypes.conductInspection, EVENTS.SUBMIT_INSPECTION);
        gtm.dataLayer().push({
          event: 'inspectionConducted',
        });

        if (isDraft) {
          toasted.success(t('inspections.draft-saved'));
          pushRoute({ name: 'forms', query: { tab: 'draft' } });
          return;
        }

        const routeLocation: NamedLocation = hasPermission(Permissions.VIEW_OWN_INSPECTION)
          ? { name: 'inspectionDetail', params: { id: createdId.toString(), showConfetti: 'true' } }
          : { name: 'home' };

        if (!isDraft) {
          Userflow.trackEvent(UserflowTrackEvent.FORM_SUBMITTED);
        }
        toasted.success(t('inspections.created'));
        pushRoute(routeLocation);
      },
      undefined,
      error => {
        if (error.code === 403) {
          toasted.error(t('error.403'));
          return pushRoute({ name: 'forms', query: { [queryTabName]: FormsTabNames.SUBMITTED } });
        }

        return null;
      },
    );

    const sendRecordTask = sendRecord(false);
    const sendRecordDraftTask = sendRecord(true);
    const photoAssetsLoading = ref(false);
    const fileAssetsLoading = ref(false);
    const submitIsLoading = computed(
      () => sendRecordTask.state.value.pending
        || sendRecordDraftTask.state.value.pending
        || photoAssetsLoading.value
        || fileAssetsLoading.value,
    );

    const onSubmit = async ({ inspection }: { inspection: LegacyChecklistRecord }) => {
      mapEntityTypeOfResponseFirstQuestion(inspection);

      record.value = inspection;
      record.value!.conducted_at = now();
      record.value!.status = 10;

      photoAssetsLoading.value = true;
      await inspectionAssetsPhotos.processPhotos(record.value);
      photoAssetsLoading.value = false;

      fileAssetsLoading.value = true;
      await processFiles(record.value);
      fileAssetsLoading.value = false;

      sendRecordTask.run();
    };

    const { mapEntityTypeOfResponseFirstQuestion } = useEntityTypeOfResponseFirstQuestionMapping();

    const onSubmitDraft = async ({ inspection }: { inspection: LegacyChecklistRecord }) => {
      mapEntityTypeOfResponseFirstQuestion(inspection);

      record.value = inspection;
      record.value!.last_updated = now();

      photoAssetsLoading.value = true;
      await inspectionAssetsPhotos.processPhotos(record.value);
      photoAssetsLoading.value = false;

      fileAssetsLoading.value = true;
      await processFiles(record.value);
      fileAssetsLoading.value = false;

      sendRecordDraftTask.run();
    };

    const detachEventListeners = () => {
      EventBus.$off(Events.submittedInspection, onSubmit);
      EventBus.$off(Events.submittedDraftInspection, onSubmitDraft);
      conductStore.$reset();
    };

    EventBus.$on(Events.submittedInspection, onSubmit);
    EventBus.$on(Events.submittedDraftInspection, onSubmitDraft);

    onUnmounted(detachEventListeners);

    return function render() {
      return loader('div', getRecord, [
        record.value ? h(InspectionNavbar, {
          props: {
            record: record.value,
            loading: submitIsLoading.value,
            saving: sendRecordDraftTask.state.value.pending,
            submitting: sendRecordTask.state.value.pending,
          },
        }) : null,
        container([
          downloadApp(),
          record.value ? h(ConductInspection,
            {
              class: 'legacy',
              props: {
                resource: record.value,
                assignees: userOptions.value,
                loading: submitIsLoading.value,
                userDetail: loggedInUserDetail.value?.data,
              },
            }) : null,
        ], 'large'),
      ], false, 'create-inspection-page');
    };
  },
});
